/**
 * useAIPersonas Hook
 *
 * Hooks for AI persona management operations.
 * Feature: 071-ai-critique-sessions (US2)
 *
 * NOTE: Admin CRUD operations use GraphQL (re-exported from useAdminAIPersonasGraphQL).
 * The export endpoint uses REST since it returns a file download.
 */

import { useState, useCallback } from 'react';
import { useQuery, gql } from '@apollo/client';
import { api } from '@/services/api';
import logger from '@/utils/logger';

// Re-export GraphQL hooks for admin CRUD operations
export {
  useAdminAIPersonasGraphQL as useAIPersonas,
  useAdminAIPersonaGraphQL as useAIPersona,
  useAdminCreateAIPersonaGraphQL as useCreateAIPersona,
  useAdminUpdateAIPersonaGraphQL as useUpdateAIPersona,
  useAdminDeleteAIPersonaGraphQL as useDeleteAIPersona,
  useAdminImportAIPersonaGraphQL as useImportAIPersona,
  AI_PROVIDERS,
  AI_MODELS,
  getProviderDisplayName,
} from './graphql/useAdminAIPersonasGraphQL';

// Re-export types
export type {
  AIPersona,
  AIPersonasResponse,
  CreateAIPersonaInput,
  UpdateAIPersonaInput,
  ImportResult,
} from './graphql/useAdminAIPersonasGraphQL';

// GraphQL query for public available personas
const AVAILABLE_AI_PERSONAS_QUERY = gql`
  query AvailableAiPersonas {
    availableAiPersonas
  }
`;

/**
 * Hook: Export AI persona as markdown (REST endpoint - file download)
 */
export function useExportAIPersona() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const exportPersona = useCallback(async (personaId: string): Promise<{ markdown: string; filename: string }> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/admin/ai-personas/${personaId}/export`, {
        responseType: 'text',
      });

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : 'persona.md';

      logger.info('Admin', 'Exported AI persona', { id: personaId });
      return { markdown: response.data, filename };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to export AI persona');
      setError(error);
      logger.error('Admin', 'Failed to export AI persona', err);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { exportPersona, loading, error };
}

/**
 * Available AI Persona type (public, no prompt)
 */
export interface AvailableAIPersona {
  id: string;
  globalId: string;
  username: string;
  slug: string;
  displayName: string;
  provider: 'openai' | 'anthropic' | 'google';
  model: string;
}

interface AvailableAIPersonasResponse {
  personas: AvailableAIPersona[];
  total: number;
}

/**
 * Hook: Fetch available AI personas (public, no prompts)
 * Used for session creation persona selection.
 *
 * Uses cache-and-network policy: shows cached data immediately for better UX,
 * then updates from network. Personas rarely change, so stale data is acceptable
 * for a brief moment. Admin hooks use network-only since admins need fresh data.
 */
export function useAvailableAIPersonas() {
  const { data, loading, error, refetch } = useQuery<{ availableAiPersonas: AvailableAIPersonasResponse }>(
    AVAILABLE_AI_PERSONAS_QUERY,
    {
      // cache-and-network: show cached immediately, refresh in background
      // (Admin hooks use network-only for always-fresh data)
      fetchPolicy: 'cache-and-network',
      onError: (err) => {
        logger.error('UI', 'Failed to fetch available AI personas', err);
      },
    }
  );

  return {
    data: data?.availableAiPersonas,
    loading,
    error,
    refetch,
    isLoading: loading,
    isError: !!error,
  };
}
